import { z } from "zod";
import { OffersEmbedSchema } from "../upsell-constructs";

// =========================================================================
// GET /admin/forms/:id - RESPONSE (Editor Load)
// =========================================================================

// =========================================================================
// GET & PUT /admin/forms/:id (EDITOR)
// =========================================================================

// 1. DISEÑO

// DEFINICIÓN DE ESTILOS DE COMPONENTES
const ComponentStylesSchema = z.object({
  shipping_method: z
    .object({
      background_color: z.string().optional(),
      border_color: z.string().optional(),
      active_border_color: z.string().optional(),
      text_color: z.string().optional(),
      show_radio_button: z.boolean().optional(),
    })
    .optional(),

  order_summary: z
    .object({
      background_color: z.string().optional(),
      subtotal_text: z.string().optional(),
      shipping_text: z.string().optional(),
      total_text: z.string().optional(),
      show_tax_notice: z.boolean().optional(),
      border_color: z.string().optional(),
      border_width: z.number().optional(),
      border_radius: z.number().optional(),
    })
    .optional(),

  submit_button: z
    .object({
      text: z.string().optional(),
      background_color: z.string().optional(),
      text_color: z.string().optional(),
      border_radius: z.number().optional(),
    })
    .optional(),
});

export const FormDesignSettingsSchema = z.object({
  template_id: z.string(), // form_design_templates.id
  settings: z
    .object({
      // Campos que el editor debe mostrar controls visuales
      layout_mode: z.enum(["embedded", "popup"]).optional(),
      template_slug: z.string().optional(),
      primary_color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
      background_color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
      font_family: z.string().optional(),
      corner_radius: z.number().int().min(0).max(50).optional(),
      show_logo: z.boolean().optional(),
      components: ComponentStylesSchema.optional(),
    })
    .loose(), // Permite guardar logo_url, etc.
});

// 2. CAMPOS (Polimórfico - Espejo del Bootstrap)
const AdminBaseFieldSchema = z.object({
  key: z.string(), // Inmutable (field_key)
  label: z.string(), // Editable (va a form_field_overrides.config)
  visible: z.boolean(), // Editable
  display_order: z.number().int(), // Mandatory per user requirements
  locked: z.boolean().optional(), // Si el usuario puede borrarlo
  field_background_color: z.string().optional(), // Per-field bg override
});

// Variante Input
const AdminInputFieldSchema = AdminBaseFieldSchema.extend({
  type: z.enum([
    "text",
    "tel",
    "email",
    "textarea",
    "select",
    "checkbox",
    "date",
    "number",
  ]),
  placeholder: z.string().optional(),
  required: z.boolean(),
  // Propiedades avanzadas editables
  source: z.enum(["static", "remote_geo_json"]).optional(),
  options: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .optional(),
  min_length: z.number().int().min(0).optional(),
  max_length: z.number().int().min(0).optional(),
  show_icon: z.boolean().optional(),
  border_color: z.string().optional(),
  error_message: z.string().optional(),
  internal_text: z.string().optional(), // For internal Reference
});

// Variantes Estructurales (No son inputs de usuario, pero son bloques arrastrables)
const AdminStructuralFieldSchema = AdminBaseFieldSchema.extend({
  type: z.enum(["order_summary", "shipping_method"]),
  required: z.boolean().optional(), // No aplica, pero mantenemos consistencia opcional
  border_color: z.string().optional(),
});

// Variante Embed de Offers
const OffersEmbedFieldEditSchema = OffersEmbedSchema;

export const FormFieldEditSchema = z.discriminatedUnion("type", [
  AdminInputFieldSchema,
  AdminStructuralFieldSchema,
  OffersEmbedFieldEditSchema,
]);

export const ShippingRuleActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("free_shipping") }),
  z.object({ type: z.literal("flat_rate"), cost: z.number().nonnegative() }),
  z.object({
    type: z.literal("percentage_off"),
    percentage: z.number().min(0).max(100),
  }),
]);

export const FormGeneralSettingsSchema = z.object({
  thank_you_message: z.string().optional(),
  redirect_url: z.string().optional(),
  pixel_events_enabled: z.boolean().optional(),
  offices: z
    .object({
      enabled: z.boolean().optional(),
    })
    .optional(),
});

// RESPUESTA COMPLETA DEL EDITOR
export const FormDetailResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  is_active: z.boolean(),
  country_code: z.string(),

  design: FormDesignSettingsSchema,

  fields: z.array(FormFieldEditSchema),

  settings: FormGeneralSettingsSchema,
  shop_name: z.string().optional(),
});

// =========================================================================
// PUT /admin/forms/:id - REQUEST
// =========================================================================

export const UpdateFormRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  is_active: z.boolean().optional(),
  design: FormDesignSettingsSchema.optional(),
  fields: z.array(FormFieldEditSchema).optional(),
  settings: FormGeneralSettingsSchema.optional(),
});

// =========================================================================
// GET /admin/design-templates - RESPONSE
// =========================================================================

export const DesignTemplateSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  thumbnail_url: z.string().url(),
  is_premium: z.boolean(),
  tags: z.array(z.string()),
});

export const DesignTemplatesResponseSchema = z.object({
  templates: z.array(DesignTemplateSchema),
});

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type FormDetailResponse = z.infer<typeof FormDetailResponseSchema>;
export type UpdateFormRequest = z.infer<typeof UpdateFormRequestSchema>;
export type DesignTemplatesResponse = z.infer<
  typeof DesignTemplatesResponseSchema
>;
